import { useMemo } from 'react';

import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import {
  Modal,
  useModalContext,
  useModalWrapperContext,
  Button,
} from '@app-velocity/ui';
import { ShareReportPasscode } from '@app-velocity/modules/password-sharing';

import { CopyIcon } from '@app-velocity/icons';
import { MessageBus } from '@app-velocity/message-bus';

import { ShareReportLink } from '../ShareReportLink';
import { usePrototypePermissions } from '../../../../hooks/usePrototypePermissions';

type ShareFigmaReportModalContentProps = {
  shareToken: string;
  passcode: string;
  prototypeId: string;
  link: string;
};

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: theme.spacing(125),
  width: '100%',
}));

const isSharedTokenData = (
  data: unknown
): data is ShareFigmaReportModalContentProps => {
  return (
    !!(data as ShareFigmaReportModalContentProps).link ||
    !!(data as ShareFigmaReportModalContentProps).prototypeId
  );
};

const messageBus = new MessageBus('prototypes');

export const ShareFigmaReportModalContent = () => {
  const { closeModal } = useModalContext();

  const { modalData, openedModalId } = useModalWrapperContext();

  const { t } = useI18n();

  const { prototypeId, link, shareToken } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isSharedTokenData(currentModalData)) {
      return {
        shareToken: currentModalData.shareToken,
        prototypeId: currentModalData.prototypeId,
        link: currentModalData.link,
      };
    }

    return {
      shareToken: '',
      passcode: '',
      prototypeId: '',
      link: '',
    };
  }, [modalData, openedModalId]);

  const { canManageAccess } = usePrototypePermissions(shareToken);

  const handleCopyAndClose = () => {
    navigator.clipboard.writeText(link);
    closeModal();
    messageBus.publish('modal:report-link-copied:open', { mode: 'auto' });
  };

  return (
    <Modal data-testid="share-report-modal">
      <Modal.Header title="reports:share_report" />
      <StyledContent>
        <Modal.Content>
          <ShareReportLink shareLink={link} />
          {canManageAccess ? (
            <ShareReportPasscode prototypeId={prototypeId} />
          ) : null}
        </Modal.Content>
      </StyledContent>
      <Modal.Controls>
        <Button
          onClick={handleCopyAndClose}
          color="primary"
          variant="contained"
          fullWidth
        >
          <CopyIcon />
          {t('reports:share')}
        </Button>
      </Modal.Controls>
    </Modal>
  );
};
